import { useState } from 'react';

/**
 * `fill`  - grows 0% -> 100%, anchored left (the leading edge moves right).
 * `drain` - shrinks 100% -> 0%, anchored right, so the bar empties from the
 *           left and its edge also travels rightward. Keeping both phases
 *           moving the same direction reads as one continuous sweep rather
 *           than a rewind when the rest window opens.
 */
export type RestFillMode = 'fill' | 'drain';

interface Props {
  /** Length of the countdown segment in seconds. 0 pins the fill full. */
  total: number;
  /** Seconds already elapsed in the segment when this fill mounted. */
  elapsed: number;
  mode: RestFillMode;
}

/**
 * The sweeping background fill for the rest timer.
 *
 * The animation parameters are captured **once on mount** and never updated.
 * The parent re-renders about four times a second to tick the countdown text,
 * and re-applying `animationDelay` on a running animation makes the browser
 * re-seek it — which showed up as a visible jump every second. Mount this with
 * `key={phase}` so a fresh instance (and a fresh sweep) starts per phase.
 */
export default function RestFill({ total, elapsed, mode }: Props) {
  // Lazy initialiser: runs on mount only, so later prop ticks are ignored.
  const [anim] = useState(() => ({ total, elapsed }));

  if (anim.total <= 0) {
    return <div className="rest-fill rest-fill--full" aria-hidden="true" />;
  }

  return (
    <div
      className={`rest-fill rest-fill--${mode}`}
      aria-hidden="true"
      style={{
        animationDuration: `${anim.total}s`,
        // Negative delay seeks the sweep to where it already is, so resuming
        // mid-rest picks up in the right place instead of restarting.
        animationDelay: `-${anim.elapsed}s`,
      }}
    />
  );
}
